<!--
// app/components/pages/legends/CardLegendPlayer.vue
-->
<script setup lang="ts">
import type { Team } from "#shared/team/domain/models/Team";
import type { PlayerSeasons } from "#shared/player/domain/Player";
import { useImages } from "~/composables/images";
import { usePlayers } from "~/composables/players";
import { setAliasUrl } from "#shared/Shared/utils";
import { initModals } from "flowbite";
import BaseVideoModal from "~/components/base/BaseVideoModal.vue";

// initialize components based on data attribute selectors
onMounted(() => {
  initModals();
});

type Props = {
  playerId: number;
  preload?: boolean;
};

const props = defineProps<Props>();

const { playerPlaceholderPath } = useImages();
const { useFetchParamsPlayerSeasons } = usePlayers();

const fetchPlayerOpts = useFetchParamsPlayerSeasons(props.playerId);
const { data } = await useFetch(fetchPlayerOpts);
const playerData: PlayerSeasons | undefined = data.value ? data.value : undefined;

const modalActive = ref(false);

const team: Team | undefined =
  playerData && playerData.seasons.find(s => s.team.countryId === 13)
    ? playerData.seasons.find(s => s.team.countryId === 13)?.team
    : playerData && playerData?.seasons.length
      ? playerData.seasons[0].team
      : undefined;
const playerImage: string | undefined =
  playerData && playerData.seasons.find(s => s.team.countryId === 13)
    ? playerData.seasons.find(s => s.team.countryId === 13)?.playerImage.url
    : playerData && playerData?.seasons.length
      ? playerData.seasons[0].playerImage.url
      : undefined;
const videoPlayerID: string | undefined = playerData?.metaInformation?.legend_yt_video_id;
const defaultColor = "#888888";
</script>

<template>
  <component
    :is="videoPlayerID ? 'a' : 'div'"
    v-if="playerData"
    class="card-container"
    type="button"
    :data-modal-target="videoPlayerID ? `video${playerId}` : undefined"
    :data-modal-show="videoPlayerID ? `video${playerId}` : undefined"
    @click="modalActive = true"
  >
    <div class="relative h-[calc(100%-90px)]">
      <NuxtImg
        :src="playerImage ? setAliasUrl(playerImage) : playerPlaceholderPath"
        :alt="playerData.shortName || ''"
        :preload="preload"
        width="456"
        height="642"
        sizes="299px sm:336px"
        class="absolute z-[3] w-full player-img"
      />
      <div
        style="background-image: linear-gradient(rgba(0, 0, 0, 0), rgba(0, 0, 0, 0.5))"
        class="absolute w-full h-1/2 bottom-0 z-[4]"
      ></div>
      <div class="absolute z-[2] top-0 left-0">
        <div
          class="clip-diagonal-right"
          :style="`background-image: linear-gradient(180deg, ${
            team && team.firstColorHEX ? team?.firstColorHEX : defaultColor
          }B3 0%, #1c1c1cCC 90%);`"
        >
          <NuxtImg
            v-if="team"
            :src="setAliasUrl(team.logo.url)"
            :alt="team.completeName || ''"
            width="78"
            height="78"
            class="w-[78px] h-[78px] object-contain object-center p-2 absolute left-0 top-0"
          />
        </div>
      </div>
      <div class="z-[2] absolute bottom-0 right-0">
        <div
          class="clip-diagonal-left"
          :style="`background-image: linear-gradient(180deg, ${
            team && team.firstColorHEX ? team?.firstColorHEX : defaultColor
          }B3 0%, #1c1c1cCC 100%);`"
        ></div>
      </div>
    </div>
    <div class="bg-gray-900 h-[90px] rounded-b-lg overflow-hidden flex justify-between relative z-[5]">
      <div class="container-name-player">
        <h2 class="font-bold leading-tight">{{ playerData?.shortName }}</h2>
        <p class="text-gray-400">{{ $t(`${playerData?.role}`) }}</p>
      </div>
      <div class="absolute ltr:right-0 rtl:left-0 top-0 z-[1] h-[91px]">
        <div class="relative flex justify-end items-center max-h-full -mt-[2px]">
          <svg
            width="199"
            height="105"
            class="rtl:-scale-x-100"
            viewBox="0 0 199 89"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              id="Rectangle 1754"
              opacity="0.6"
              d="M8.18224 101.097C1.07435 100.971 -2.35052 92.3293 2.74124 87.3682L91.6853 0.707364L244 0.70734L244 97.1356C244 101.609 240.331 105.214 235.858 105.134L8.18224 101.097Z"
              fill="url(#paint0_linear_3332_2365)"
            />
            <defs>
              <linearGradient
                id="paint0_linear_3332_2365"
                x1="69.7747"
                y1="9.41294"
                x2="215.118"
                y2="137.939"
                gradientUnits="userSpaceOnUse"
              >
                <stop offset="0" stop-color="#F4AF23" />
                <stop offset="1" stop-color="#FCEFCE" />
              </linearGradient>
            </defs>
          </svg>
          <div
            v-if="videoPlayerID"
            class="w-11 h-11 p-3.5 bg-black rounded-full inline-flex justify-center items-center absolute me-6"
          >
            <svg
              class="play-icon"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 16 18"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M1 1.984v14.032a1 1 0 0 0 1.506.845l12.006-7.016a.974.974 0 0 0 0-1.69L2.506 1.139A1 1 0 0 0 1 1.984Z"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </component>

  <base-video-modal
    v-if="videoPlayerID"
    :modal-id="`video${playerId}`"
    :video-id="videoPlayerID"
    v-model="modalActive"
  ></base-video-modal>
</template>

<style scoped>
.card-container {
  @apply border border-primary rounded-lg w-full sm:w-[336px] max-w-[336px] min-h-[437px] flex flex-col justify-end overflow-hidden;
  background-image:
    linear-gradient(0deg, rgba(0, 0, 0, 0.6) 100%, rgba(0, 0, 0, 0.6) 100%),
    url("/img/bg-card/bg-card-legends.jpg");
  background-size: 80% auto;
}

.container-name-player {
  @apply flex flex-col items-start justify-center min-h-full ps-6 py-6 absolute z-[2];
}

.card-container:hover .container-name-player {
  @apply ps-7;
}

.play-icon {
  @apply w-4 h-4 text-gray-500;
}

.card-container:hover .play-icon {
  @apply text-primary;
}

.clip-diagonal-right {
  @apply h-[135px] w-[135px] overflow-hidden relative;
  clip-path: polygon(0 0, 100% 0%, 0% 100%, 0% 100%);
}

.clip-diagonal-left {
  @apply h-[150px] w-[150px] overflow-hidden relative;
  clip-path: polygon(100% 0, 100% 0%, 100% 100%, 0% 100%);
}

.clip-diagonal-left::after,
.clip-diagonal-right::after {
  content: "";
  @apply absolute bg-white bg-opacity-10 w-full h-full translate-y-full;
}

.container-name-player,
.play-icon,
.clip-diagonal-right::after,
.clip-diagonal-left::after,
.player-img {
  @apply transition-all ease-in-out duration-300;
}

.card-container:hover .player-img {
  @apply scale-[1.02];
}

.card-container:hover .clip-diagonal-right::after {
  @apply -translate-y-[160%];
}

.card-container:hover .clip-diagonal-left::after {
  @apply -translate-y-full;
}
</style>
